<template>
  <Loader :show="pageLoading" v-if="pageLoading" />
  <MDBCard class="border-0" v-else>
    <CategoryTop small :title="$t('account.profile.title')" :breadcrumbs-parent-routes="['account']"
      :description="$t('account.profile.description')" content-class="px-4 mt-4" left-col="6" right-col="6">
      <template #right>
        <MDBRow>
          <MDBCol col="12" class="mb-5">
            <div class="progress">
              <div class="progress-bar bg-danger progress-bar-striped progress-bar-animated" role="progressbar"
                :style="'width:' + fillPercent + '%'" :aria-valuenow="fillPercent" aria-valuemin="0"
                aria-valuemax="100">
                {{ fillPercent }}%
              </div>
            </div>
          </MDBCol>
        </MDBRow>
      </template>
    </CategoryTop>

    <MDBTabs v-model="activeTabId">
      <MDBRow>
        <MDBCol md="12" class="px-5 pb-4">
          <MDBTabNav tabsClasses="profile-tabs" pills>
            <MDBTabItem tabId="general" href="general" class="text-center py-1 " :class="`${activeTabId === 'general'
              ? 'bg-dark text-white'
              : 'bg-secondary-gray text-dark'
              }`">
              <div class="item--height d-flex justify-content-center align-items-center"
                v-html="$t('account.profile.general')"></div>
            </MDBTabItem>
            <MDBTabItem tabId="preference" href="preference" class="text-center py-1" :class="`${activeTabId === 'preference'
              ? 'bg-dark text-white'
              : 'bg-secondary-gray text-dark'
              }`">
              <div class="item--height d-flex justify-content-center align-items-center">{{
                $t("account.profile.activityAndProfile") }}
              </div>
            </MDBTabItem>
          </MDBTabNav>
        </MDBCol>
      </MDBRow>

      <MDBTabContent>
        <MDBTabPane tabId="general">
          <VeeForm class="form user-form" @submit="onSubmit">
            <MDBRow>
              <MDBCol xl="4" md="6" class="mb-4">
                <PersonalData ref="personalData"></PersonalData>
              </MDBCol>

              <MDBCol xl="4" md="6" class="mb-4">
                <OperatingData ref="operatingData"></OperatingData>
              </MDBCol>

              <MDBCol xl="3" md="6" class="mb-4">
                <div>
                  <label for="legalForm" class="label px-3 text-uppercase pt-5">
                    {{ $t("account.profile.legalForm") }}
                  </label>
                  <input type="text" class="form-control" :placeholder="$t('account.profile.legalForm')"
                    :aria-label="$t('account.profile.legalForm')" aria-describedby="basic-addon1" v-model="legalForm"
                    id="legalForm" />
                </div>

              </MDBCol>
            </MDBRow>

            <MDBRow>
              <MDBCol col="12" class="mb-4">
                <IndicatorsData ref="indicatorsData"></IndicatorsData>
              </MDBCol>
              <MDBCol col="12" class="mb-4">
                <Field type="checkbox" name="conditions" v-model="conditions" :value="true" :unchecked-value="false"
                  class="form-check-input" :label="$t('account.profile.acceptTerms')" :rules="{ required: true }">
                </Field>
                <span class="px-2 text-decoration-underline--hover cursor--pointer">{{
                  $t("account.profile.acceptTerms") }}</span>
                <div>
                  <ErrorMessage name="conditions" class="text-danger text-small text-end m-0 pt-1" />
                </div>
              </MDBCol>
            </MDBRow>
            <MDBRow>
              <MDBCol col="12" class="text-start">
                <div v-html="$t('account.profile.newCustomerNumbersInfo')" class="pb-3">
                </div>
                <div>
                  <div
                    v-if="me.salespersons_dealers && me.salespersons_dealers.length > 0 && tenantConfig.showBsStaffAtProfile"
                    class="pb-4">
                    <div v-for="(seller, index) in me.salespersons_dealers" :key="index">
                      <div v-if="!seller.is_internal">
                        <span class="fw-bold">{{ $t("contactService.contactPerson") }}:</span>
                        {{ seller.name }}
                        <a :href="'mailto:' + seller.email">{{ seller.email }}</a>
                      </div>
                    </div>
                  </div>
                  <div v-else>
                    {{ $t('account.profile.contactInfo', { email: tenantConfig.contactEmail }) }}
                  </div>
                </div>

                <MDBBtn type="submit" color="danger" :disabled="loading"
                  class="d-flex justify-content-center align-items-center">
                  <span>{{ $t("global.buttons.send") }}</span>
                  <Loader :show="loading" :small="true" spinner="spinner.svg" :tiny="true" />
                </MDBBtn>
                <p v-if="apiError" class="text-danger text-small text-end m-0 pt-2 text-small">
                  {{ apiError }}
                </p>
              </MDBCol>
            </MDBRow>
          </VeeForm>
        </MDBTabPane>
        <MDBTabPane tabId="preference">
          <LiteVersionPreferences ref="preferencesRef" @requestSave="onSubmit" @errorMessage="onErrorMessage"
            @customerNumberExists="activeTabId = 'preference'" />
        </MDBTabPane>
      </MDBTabContent>

    </MDBTabs>
  </MDBCard>
</template>

<script setup>
import { Form as VeeForm, Field, ErrorMessage } from "vee-validate";
import CategoryTop from "@/components/General/CategoryTop/index.vue";
import { MDBRow, MDBCol, MDBCard, MDBBtn, MDBTabs, MDBTabNav, MDBTabItem, MDBTabContent, MDBTabPane } from "mdb-vue-ui-kit";
import { useStore } from "vuex";
import { ref, onMounted, computed, watch } from "vue";
import { toast } from "vue3-toastify";
import PersonalData from "@/components/User/ProfileForms/PersonalData.vue";
import OperatingData from "@/components/User/ProfileForms/OperatingData.vue";
import IndicatorsData from "@/components/User/ProfileForms/IndicatorsData.vue";
import Loader from "@/components/General/Loader/index.vue";

import "vue3-toastify/dist/index.css";
import { useI18n } from "vue-i18n";

import { afterSuccess } from "@/composables/profile.js";
import { useProfileFormData } from "@/composables/useProfileFormData";
import { useProfileFillPercent } from "@/composables/useProfileFillPercent";
import LiteVersionPreferences from "../../ProfileForms/LiteVersionPreferences.vue";
import { useTenantConfig } from "@/composables/useTenantConfig";

const { tenantConfig } = useTenantConfig();

const store = useStore();
const { me, profile, userHashId, profileHashId, userPlainId, loadProfileForm } =
  useProfileFormData(store);
const { t } = useI18n();
const personalData = ref(null);
const operatingData = ref(null);
const indicatorsData = ref(null);
const apiError = ref(null);
const legalForm = ref(null);
const isWholesaler = ref(false);
const isWholesalerStaff = ref(false);
const isStaff = ref(false);
const conditions = ref(false);
const errors = ref(false);
const loading = computed(() => store.getters.getPending);
const pageLoading = ref(true);

const activeTabId = ref('general')
const preferencesRef = ref(null);

const onErrorMessage = (message) => {
  apiError.value = message;
};

const onSubmit = async () => {
  const userId = userHashId.value;
  if (!userId) {
    apiError.value = t("toast.apiError");
    return;
  }

  const { vat, email, ...personal } = personalData.value.models;
  const { ...operating } = operatingData.value.models;
  const { lang, ...mainAddress } = operating;

  let args = {
    id: userId,
    user: {
      email,
      country: operating.country,
      lang,
      vat,
      is_active: true,
    },
    profile: {
      user_id: userId,
      legalForm: legalForm.value,
      ...personal,
      ...mainAddress,
      ...indicatorsData.value.models,
      conditions: conditions.value
    },
    addresses: [mainAddress],
  };


  if (args.user.country !== "ch") {
    args.user.lang = "de";
  }

  if (args.profile.mobile_phone !== null && args.profile.mobile_phone !== undefined) {
    args.profile.mobile_phone = String(args.profile.mobile_phone);
  }

  const response = await store.dispatch("updateUser", args);

  if (response.status === 200) {
    let preferencesOk = true;
    if (preferencesRef.value?.persistPreferences) {
      preferencesOk = await preferencesRef.value.persistPreferences({
        refresh: false,
        showToast: false,
      }) !== false;
    }

    if (preferencesOk) {
      await loadProfileForm();
      apiError.value = null;
      toast(t("toast.profileSaved"), {
        type: "success",
        theme: "colored",
        transition: toast.TRANSITIONS.SLIDE,
        position: toast.POSITION.BOTTOM_RIGHT,
      });
    }

    await afterSuccess(
      userHashId.value,
      profileHashId.value,
      fillPercent.value,
      userPlainId.value,
      store
    );
  } else {
    apiError.value = response.data.message;
  }
};

const { fillPercent } = useProfileFillPercent(() => {
  const legalFormPercentage = legalForm.value ? 100 : 0;

  return [
    personalData.value?.percentage,
    operatingData.value?.percentage,
    indicatorsData.value?.percentage,
    legalFormPercentage,
  ];
});

watch(me, async (newVal) => {
  if (newVal && newVal.roles.find((item) => item.name == "wholesaler")) {
    isWholesaler.value = true;
  }
  if (newVal && newVal.roles.find((item) => item.name == "wholesalerStaff")) {
    isWholesalerStaff.value = true;
  }
  if (newVal && newVal.roles.find((item) => item.name == "wholesaler")) {
    isStaff.value = true;
  }
});

watch(
  () => indicatorsData.value?.errors,
  (newVal) => {
    if (Object.values(newVal).findIndex((item) => item == true) == -1) {
      errors.value = false;
    } else {
      errors.value = true;
    }
  },
  {
    deep: true,
  }
);

watch(profile, (newVal) => {
  if (!newVal) return;
  legalForm.value = newVal.legalForm;
});


onMounted(async () => {
  try {
    await loadProfileForm();
  } finally {
    pageLoading.value = false;
  }
});

</script>

<style scoped lang="scss">
@import "@/styles/_variables.scss";
@import "@/styles/profile.scss";

.item--height {
  min-height: 40px !important;
}
</style>
